import React, { useEffect, useState } from 'react';
import { Play, Square, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useProject } from '@/lib/ProjectContext';
import { useModules, hasModule } from '@/lib/modules';
import { timeAPI, fmtHours } from '@/lib/financeAPI';
import ContactSelect from './ContactSelect';

const sel = 'w-full h-10 rounded-md border border-input px-2 text-sm bg-white';
const blank = { activity: '', description: '', project_id: '', contact_id: '', contact_name: '', billable: true };

const clock = (secs) => {
  const h = Math.floor(secs / 3600); const m = Math.floor((secs % 3600) / 60); const s = Math.floor(secs % 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Start/stop timer in the top bar, so time can be caught while you work on anything in the app.
export default function TimerPill() {
  const mods = useModules();
  const on = hasModule(mods, 'time');
  const { projects = [] } = useProject() || {};
  const [timer, setTimer] = useState(null);
  const [form, setForm] = useState(blank);
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  const load = () => timeAPI.timer().then((r) => {
    setTimer(r.data.running ? r.data : null);
    if (r.data.running) setForm({ activity: r.data.activity || '', description: r.data.description || '', project_id: r.data.project_id || '',
      contact_id: r.data.contact_id || '', contact_name: r.data.contact_name || '', billable: r.data.billable !== false });
  }).catch(() => {});

  useEffect(() => {
    if (!on) return undefined;
    load();
    timeAPI.settings().then((r) => setActivities(r.data.activities || [])).catch(() => {});
    const poll = setInterval(load, 60000);
    window.addEventListener('time-changed', load);
    return () => { clearInterval(poll); window.removeEventListener('time-changed', load); };
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!timer) return undefined;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [timer]);

  if (!on) return null;
  const elapsed = timer ? Math.max(0, (nowMs - new Date(timer.started_at).getTime()) / 1000) : 0;
  const set = (patch) => setForm({ ...form, ...patch });
  const body = () => ({ ...form, project_id: form.project_id || null, contact_id: form.contact_id || null });

  const start = async () => {
    setBusy(true);
    try { const r = await timeAPI.start(body()); setTimer(r.data); setOpen(false); toast.success('Timer started'); window.dispatchEvent(new Event('time-changed')); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the timer'); } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try {
      const r = await timeAPI.stop(body());
      setTimer(null); setForm(blank); setOpen(false);
      toast.success(`Logged ${fmtHours(r.data.hours)}${r.data.project_name ? ` to ${r.data.project_name}` : ''}`);
      window.dispatchEvent(new Event('time-changed'));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not stop the timer'); } finally { setBusy(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {timer ? (
          <button className="flex items-center gap-2 h-9 px-3 rounded-full bg-[#1E2A33] text-white text-sm font-semibold tabular-nums" title="Timer running">
            <span className="w-2 h-2 rounded-full bg-[#F0B56E] animate-pulse" />{clock(elapsed)}
            <span className="hidden lg:inline font-normal text-white/70 max-w-[160px] truncate">{timer.project_name || timer.description || ''}</span>
          </button>
        ) : (
          <Button variant="outline" size="sm" className="rounded-full" title="Start a timer"><Timer className="w-4 h-4 md:mr-1" /><span className="hidden md:inline">Timer</span></Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-2">
        <div className="text-sm font-semibold text-[#1E2A33]">{timer ? `Running · ${clock(elapsed)}` : 'What are you working on?'}</div>
        <Input placeholder="What you're doing" value={form.description} onChange={(e) => set({ description: e.target.value })} />
        <Input list="timer-activities" placeholder="Activity (e.g. Prep & research)" value={form.activity} onChange={(e) => set({ activity: e.target.value })} />
        <datalist id="timer-activities">{activities.map((a) => <option key={a} value={a} />)}</datalist>
        <select className={sel} value={form.project_id} onChange={(e) => set({ project_id: e.target.value })}>
          <option value="">No project</option>{projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        <ContactSelect value={form.contact_id} label={form.contact_name} onChange={(c) => set({ contact_id: c?.id || '', contact_name: c?.label || '' })} />
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.billable} onCheckedChange={(v) => set({ billable: v })} />Billable</label>
        {timer ? (
          <div className="flex gap-2 pt-1">
            <Button className="flex-1 bg-[#A62B1F] hover:bg-[#8C2318] text-white" onClick={stop} disabled={busy}><Square className="w-4 h-4 mr-1" />Stop and log</Button>
            <Button variant="ghost" size="icon" title="Discard this timer" disabled={busy} onClick={async () => {
              if (!window.confirm('Discard this timer without logging it?')) return;
              await timeAPI.discard(); setTimer(null); setForm(blank); setOpen(false); window.dispatchEvent(new Event('time-changed'));
            }}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ) : (
          <Button className="w-full bg-[#A62B1F] hover:bg-[#8C2318] text-white" onClick={start} disabled={busy}><Play className="w-4 h-4 mr-1" />Start timer</Button>
        )}
        <p className="text-[11px] text-[#94A3B8]">Keeps running if you close the app. Rounded up to the nearest few minutes when you stop.</p>
      </PopoverContent>
    </Popover>
  );
}
